// src/pages/Discussion.jsx
// ─────────────────────────────────────────────────────────────────────────────
// .env (Create React App — must use REACT_APP_ prefix):
//   REACT_APP_SUPABASE_URL=https://xxxx.supabase.co
//   REACT_APP_SUPABASE_KEY=sb_publishable_xxxx
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  Send, Star, MessageSquare, User, Mail,
  CheckCircle, AlertCircle, Lock,
} from "lucide-react";

// ── Supabase client ───────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;
const SUPABASE_READY = !!SUPABASE_URL && !!SUPABASE_KEY;

const supabase = SUPABASE_READY
  ? createClient(SUPABASE_URL, SUPABASE_KEY)
  : null;

// ── Browser fingerprint ───────────────────────────────────────────────────────
const getFingerprint = () => {
  const signals = [
    navigator.userAgent,
    navigator.language,
    `${screen.width}x${screen.height}`,
    screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.hardwareConcurrency || 0,
    navigator.platform || "",
    navigator.vendor || "",
  ].join("|");
  let hash = 5381;
  for (let i = 0; i < signals.length; i++) {
    hash = (hash * 33) ^ signals.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

// ── LocalStorage fallback ─────────────────────────────────────────────────────
const LS_REVIEWS = "portfolio_reviews_v2";
const LS_FP      = "portfolio_fp_v2";

const lsLoadReviews = () => {
  try { return JSON.parse(localStorage.getItem(LS_REVIEWS) || "[]"); } catch { return []; }
};
const lsSaveReview = (r) => {
  try {
    const existing = lsLoadReviews();
    localStorage.setItem(LS_REVIEWS, JSON.stringify([r, ...existing]));
  } catch {}
};
const lsHasPosted = (fp) => {
  try {
    const stored = JSON.parse(localStorage.getItem(LS_FP) || "{}");
    if (!stored[fp]) return false;
    if (Date.now() - stored[fp] > 90 * 24 * 60 * 60 * 1000) {
      delete stored[fp];
      localStorage.setItem(LS_FP, JSON.stringify(stored));
      return false;
    }
    return true;
  } catch { return false; }
};
const lsMarkPosted = (fp) => {
  try {
    const stored = JSON.parse(localStorage.getItem(LS_FP) || "{}");
    stored[fp] = Date.now();
    localStorage.setItem(LS_FP, JSON.stringify(stored));
  } catch {}
};

// ── Data layer ────────────────────────────────────────────────────────────────
const db = {
  async loadReviews() {
    if (!SUPABASE_READY) return lsLoadReviews();
    const { data, error } = await supabase
      .from("reviews")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw error;
    return data || [];
  },

  async hasPosted(fp) {
    if (!SUPABASE_READY) return lsHasPosted(fp);
    const { data } = await supabase
      .from("fingerprints")
      .select("fp")
      .eq("fp", fp)
      .maybeSingle();
    return !!data;
  },

  async submitReview(review, fp) {
    if (!SUPABASE_READY) {
      if (lsHasPosted(fp)) return { duplicate: true };
      lsMarkPosted(fp);
      lsSaveReview(review);
      return { success: true };
    }

    // Insert fingerprint first — unique constraint = dedup guard
    const { error: fpError } = await supabase
      .from("fingerprints")
      .insert({ fp });

    if (fpError) {
      if (fpError.code === "23505") return { duplicate: true };
      throw fpError;
    }

    const { error: reviewError } = await supabase
      .from("reviews")
      .insert(review);

    if (reviewError) throw reviewError;
    return { success: true };
  },
};

// ── Star rating ───────────────────────────────────────────────────────────────
const StarRating = ({ value, onChange, readonly = false, size = 22 }) => {
  const [hovered, setHovered] = useState(0);
  const labels = ["", "Poor", "Fair", "Good", "Great", "Excellent"];
  return (
    <div className="flex items-center gap-1.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(s)}
          onMouseEnter={() => !readonly && setHovered(s)}
          onMouseLeave={() => !readonly && setHovered(0)}
          className={`transition-all duration-150 focus:outline-none ${
            readonly ? "cursor-default" : "cursor-pointer hover:scale-125"
          }`}
        >
          <Star
            size={size}
            className="transition-all duration-150"
            style={{
              fill: (hovered || value) >= s ? "#f59e0b" : "transparent",
              stroke: (hovered || value) >= s ? "#f59e0b" : "currentColor",
              opacity: readonly && value === 0 ? 0.3 : 1,
            }}
          />
        </button>
      ))}
      {!readonly && (hovered || value) > 0 && (
        <span className="ml-2 font-mono text-[11px] uppercase tracking-widest text-amber-400">
          {labels[hovered || value]}
        </span>
      )}
    </div>
  );
};

// ── Average stars ─────────────────────────────────────────────────────────────
const AverageStars = ({ avg, count }) => (
  <div className="flex items-baseline gap-3">
    <span className="font-mono text-5xl font-bold text-amber-400">{avg.toFixed(1)}</span>
    <div>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            size={16}
            style={{
              fill: s <= Math.floor(avg)
                ? "#f59e0b"
                : s === Math.floor(avg) + 1 && (avg % 1) > 0
                ? `rgba(245,158,11,${avg % 1})`
                : "transparent",
              stroke: "#f59e0b",
            }}
          />
        ))}
      </div>
      <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mt-1">
        {count} review{count !== 1 ? "s" : ""}
      </div>
    </div>
  </div>
);

// ── Review card ───────────────────────────────────────────────────────────────
const ReviewCard = ({ review, index }) => {
  const initials = review.name
    ? review.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "AN";

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const maskedEmail = review.email
    ? review.email.replace(/(.{2}).*(@.*)/, "$1…$2")
    : null;

  return (
    <article
      className="border-t hairline grid grid-cols-12 gap-4 py-8 reveal in"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="col-span-12 md:col-span-1">
        <div className="w-9 h-9 rounded-full border hairline grid place-items-center bg-muted/30 flex-shrink-0">
          <span className="font-mono text-[11px] font-bold">{initials}</span>
        </div>
      </div>
      <div className="col-span-12 md:col-span-11">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">{review.name || "Anonymous"}</span>
            {maskedEmail && (
              <span className="font-mono text-[10px] text-muted-foreground">{maskedEmail}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {review.rating > 0 && <StarRating value={review.rating} readonly size={13} />}
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
              {timeAgo(review.created_at || review.createdAt)}
            </span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{review.message}</p>
        <span className="mt-3 inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border hairline rounded-full text-muted-foreground">
          {review.type === "review" ? "Portfolio Review" : "Message"}
        </span>
      </div>
    </article>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Skeleton = () => (
  <div className="border-t hairline pt-8 animate-pulse space-y-3">
    <div className="flex gap-3 items-center">
      <div className="w-9 h-9 rounded-full bg-muted" />
      <div className="h-3 w-32 bg-muted rounded" />
    </div>
    <div className="h-3 w-full bg-muted rounded" />
    <div className="h-3 w-3/4 bg-muted rounded" />
  </div>
);

// ── Discussion page ───────────────────────────────────────────────────────────
const Discussion = () => {
  const [reviews, setReviews]               = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [mode, setMode]                     = useState("review");
  const [rating, setRating]                 = useState(0);
  const [name, setName]                     = useState("");
  const [email, setEmail]                   = useState("");
  const [message, setMessage]               = useState("");
  const [charCount, setCharCount]           = useState(0);
  const [status, setStatus]                 = useState(null);
  const [submitting, setSubmitting]         = useState(false);
  const [alreadyPosted, setAlreadyPosted]   = useState(false);
  const fp  = useRef(getFingerprint());
  const MAX = 500;

  // Debug — remove after confirming it works
  useEffect(() => {
    console.log("Supabase ready:", SUPABASE_READY);
    console.log("URL:", SUPABASE_URL);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [data, posted] = await Promise.all([
          db.loadReviews(),
          db.hasPosted(fp.current),
        ]);
        setReviews(data);
        setAlreadyPosted(posted);
      } catch (e) {
        console.error("Load error:", e);
      } finally {
        setLoadingReviews(false);
      }
    })();
  }, []);

  const ratedReviews = reviews.filter((r) => r.rating > 0);
  const avgRating    = ratedReviews.length
    ? ratedReviews.reduce((s, r) => s + r.rating, 0) / ratedReviews.length : 0;
  const ratingBars   = [5, 4, 3, 2, 1].map((s) => ({
    star: s,
    count: ratedReviews.filter((r) => r.rating === s).length,
    pct: ratedReviews.length
      ? Math.round((ratedReviews.filter((r) => r.rating === s).length / ratedReviews.length) * 100) : 0,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !message.trim()) return;
    if (mode === "review" && rating === 0) return;
    setSubmitting(true);
    setStatus(null);

    const review = {
      type:       mode,
      name:       name.trim()  || null,
      email:      email.trim() || null,
      message:    message.trim(),
      rating:     mode === "review" ? rating : 0,
      created_at: new Date().toISOString(),
    };

    try {
      const result = await db.submitReview(review, fp.current);
      if (result.duplicate) {
        setStatus("duplicate");
        setAlreadyPosted(true);
      } else {
        setReviews((prev) => [{ ...review, id: Date.now().toString() }, ...prev]);
        setAlreadyPosted(true);
        setStatus("success");
        setName(""); setEmail(""); setMessage(""); setRating(0); setCharCount(0);
      }
    } catch (err) {
      console.error("Submit error:", err);
      setStatus("error");
    }
    setSubmitting(false);
  };

  const showForm = !alreadyPosted && status !== "success";

  return (
    <div>
      {/* ── Hero ── */}
      <section className="border-b hairline relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,currentColor 0,currentColor 1px,transparent 1px,transparent 64px)," +
              "repeating-linear-gradient(90deg,currentColor 0,currentColor 1px,transparent 1px,transparent 64px)",
          }}
        />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-16 md:pb-20 relative">
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
            08 — Discussion
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <div>
              <h1 className="display-xl text-5xl md:text-8xl leading-[0.95]">
                Leave a <span className="italic font-light">mark.</span>
              </h1>
              <p className="mt-8 max-w-lg text-muted-foreground text-lg leading-relaxed">
                Reviewed the portfolio? Drop a rating. Just passing through?
                Leave a message. Every word lands directly with me.
              </p>
            </div>
            {!loadingReviews && ratedReviews.length > 0 && (
              <div className="md:justify-self-end">
                <AverageStars avg={avgRating} count={ratedReviews.length} />
                <div className="mt-5 space-y-1.5 min-w-[200px]">
                  {ratingBars.map(({ star, count, pct }) => (
                    <div key={star} className="flex items-center gap-2.5">
                      <span className="font-mono text-[10px] text-muted-foreground w-3">{star}</span>
                      <Star size={10} style={{ fill: "#f59e0b", stroke: "#f59e0b", flexShrink: 0 }} />
                      <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-400 rounded-full transition-all duration-700"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="font-mono text-[10px] text-muted-foreground w-5 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Body ── */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-16 items-start">

        {/* Reviews list */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-2xl md:text-4xl">
              {loadingReviews ? "Loading…"
                : reviews.length === 0 ? "No messages yet."
                : `${reviews.length} message${reviews.length !== 1 ? "s" : ""}.`}
            </h2>
            {!loadingReviews && reviews.length > 0 && (
              <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                Latest first
              </span>
            )}
          </div>

          {loadingReviews && [1, 2, 3].map((i) => <Skeleton key={i} />)}

          {!loadingReviews && reviews.length === 0 && (
            <div className="border-t hairline pt-16 text-center">
              <MessageSquare size={32} className="text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
                Be the first to leave a message.
              </p>
            </div>
          )}

          {!loadingReviews && reviews.map((r, i) => (
            <ReviewCard key={r.id || i} review={r} index={i} />
          ))}

          {!loadingReviews && reviews.length > 0 && <div className="border-t hairline mt-4" />}
        </div>

        {/* Form / states */}
        <div className="lg:sticky lg:top-10">

          {alreadyPosted && status !== "success" && (
            <div className="border hairline rounded-md p-8 text-center space-y-4">
              <Lock size={28} className="mx-auto text-muted-foreground opacity-40" />
              <p className="font-display text-xl">Already submitted.</p>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                You've already left a message from this device.
                Fingerprints expire after <strong>90 days</strong> — you can post again then.
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="border hairline rounded-md p-8 text-center space-y-4">
              <CheckCircle size={28} className="mx-auto text-emerald-500" />
              <p className="font-display text-2xl">Thank you.</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your {mode === "review" ? "review" : "message"} has been posted.
              </p>
            </div>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex rounded-md border hairline overflow-hidden">
                {[
                  { key: "review",  Icon: Star,          label: "Rate & Review" },
                  { key: "message", Icon: MessageSquare, label: "Just a Message" },
                ].map(({ key, Icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setMode(key)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                      mode === key ? "bg-foreground text-background" : "hover:bg-muted"
                    }`}
                  >
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>

              {mode === "review" && (
                <div className="border hairline rounded-md px-5 py-4 space-y-2">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block">
                    Your rating <span className="text-red-500">*</span>
                  </label>
                  <StarRating value={rating} onChange={setRating} size={28} />
                  {rating === 0 && (
                    <p className="font-mono text-[10px] text-muted-foreground">Click a star to rate</p>
                  )}
                </div>
              )}

              <div className="relative">
                <User size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  className="w-full bg-transparent border hairline rounded-md pl-10 pr-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30 transition-all"
                />
              </div>

              {mode === "review" && (
                <div className="relative">
                  <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="email"
                    placeholder="Your email (optional)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border hairline rounded-md pl-10 pr-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30 transition-all"
                  />
                  <p className="mt-1.5 font-mono text-[10px] text-muted-foreground px-1">
                    Shown publicly as ha…@gmail.com
                  </p>
                </div>
              )}

              <div className="relative">
                <textarea
                  placeholder={
                    mode === "review"
                      ? "What stood out? What could be better? Be honest."
                      : "Say anything — a question, a thought, a hello."
                  }
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); setCharCount(e.target.value.length); }}
                  maxLength={MAX}
                  rows={5}
                  required
                  className="w-full bg-transparent border hairline rounded-md px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30 transition-all resize-none"
                />
                <span className={`absolute bottom-3 right-4 font-mono text-[10px] ${charCount >= MAX ? "text-red-500" : "text-muted-foreground"}`}>
                  {MAX - charCount}
                </span>
              </div>

              {status === "error" && (
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <AlertCircle size={14} /> Something went wrong. Please try again.
                </div>
              )}
              {status === "duplicate" && (
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <Lock size={14} /> Already submitted from this device.
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !message.trim() || (mode === "review" && rating === 0)}
                className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-foreground text-background text-sm font-medium rounded-md hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                data-cursor-hover
              >
                {submitting ? (
                  <span className="font-mono text-[11px] uppercase tracking-widest animate-pulse">Posting…</span>
                ) : (
                  <>
                    <Send size={14} />
                    <span className="font-mono text-[11px] uppercase tracking-widest">
                      {mode === "review" ? "Post Review" : "Send Message"}
                    </span>
                  </>
                )}
              </button>

              <p className="font-mono text-[10px] text-muted-foreground text-center leading-relaxed">
                <Lock size={9} className="inline mr-1 opacity-50" />
                One per device · No IP stored · Fingerprint auto-expires in 90 days
              </p>

              {!SUPABASE_READY && (
                <p className="font-mono text-[10px] text-amber-400 text-center">
                  ⚠ Supabase not configured — using localStorage fallback
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Discussion;